import React from 'react';
import PropTypes from 'prop-types';
import { Form } from 'antd';
import { useMutation, useQuery, useNavigate, useParams } from '@common/hooks';
import { useSetBreadcrumbs } from 'works/legacy/lib/client-store';
import { createMessage } from 'works/legacy/lib/message-helper';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import {
  UpdateWorksListTemplateMutation,
  WorksListTemplateQuery,
} from 'works/graphql/works-list-template';
import { OnboardingListsForm } from 'works/pages/organization/admin-settings/templates-page/onboarding-list-templates/onboarding-lists-form';
import { ErrorHandler, LoadingState } from '@common/components';
import { ORGANIZATION_ADMIN_MENU } from 'works/pages/organization/admin-settings/organization-admin-vertical-nav-menu';

export function OnboardingListsTemplateEditPage() {
  const { id: worksListTemplateId } = useParams();

  useSetBreadcrumbs([
    {
      name: 'Admin',
      path: '/works/admin',
    },
    {
      name: 'Templates',
      path: '/works/admin/templates',
    },
    {
      name: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.LABEL,
      path: ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE,
    },
    {
      name: 'Edit Compliance List Template',
      path: `${ORGANIZATION_ADMIN_MENU.TEMPLATES__COMPLIANCE.ROUTE}/${worksListTemplateId}/edit`,
    },
  ]);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [updateWorksListTemplate] = useMutation(
    UpdateWorksListTemplateMutation,
    {
      onCompleted(response) {
        if (response.updateWorksListTemplate.errors) {
          addErrorsToForm(form, response.updateWorksListTemplate.errors);
        } else {
          createMessage('success', 'List Template has been updated!');
          navigate('/works/admin/templates/compliance-list');
        }
      },
      onError: () => {
        createMessage(
          'error',
          'Failed to update List Template. Please try again or contact your hiring partner.'
        );
      },
    }
  );

  const { loading, error, data } = useQuery(WorksListTemplateQuery, {
    variables: { id: worksListTemplateId },
  });

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorHandler error={error} log="OnboardingListsTemplateEditPage" />;
  }

  const worksListTemplate = data.worksListTemplate;
  const initialValues = {
    name: worksListTemplate.name,
    complianceDeadlineDetails: worksListTemplate.complianceDeadlineDetails,
  };

  const onFinish = (values) => {
    updateWorksListTemplate({
      variables: {
        worksListTemplateId: worksListTemplateId,
        input: {
          name: values.name,
          complianceDeadlineDetails: values.complianceDeadlineDetails,
          organizationUnitId: values.organizationUnitId,
        },
      },
    });
  };

  return (
    <OnboardingListsForm
      onFinish={onFinish}
      form={form}
      initialValues={initialValues}
      submitLabel="Update"
    />
  );
}

OnboardingListsTemplateEditPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};
